import { Block, CheckCircle, Edit, FileCopy, GetApp, History } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import {
  BottomNavigation,
  Box,
  Button,
  ButtonProps,
  Menu,
  MenuList,
  styled,
  Tooltip,
  Typography,
  Chip,
} from '@mui/material';
import MenuItem from '@mui/material/MenuItem';
import useDeliverableRights from 'hooks/rights/useDeliverableRights';
import useUserRoles from 'hooks/useUserRoles';
import React, { useCallback, useMemo, useState, useEffect, MouseEvent, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { selectAllDeliverablesAction, selectDeliverablesAction } from 'store/actions/deliverableActions';
import { openDialogAction } from 'store/actions/dialogsActions';
import { openDrawerAction } from 'store/actions/drawerActions';
import { addLoadingAction, removeLoadingAction } from 'store/actions/loadingsActions';
import { useSelector } from 'store/hooks';
import { useAppState } from 'store/Provider';
import { selectSelectedDeliverables as duplicateSelectSelectedDeliverables } from 'store/selectors/deliverableSelectors';
import { DeliverableTableType } from 'types/enums/DeliverableTableType';
import useDeliverableService from 'features/Deliverables/hooks/useDeliverableService';
import { MissionAdvancementPossibilities } from 'types/enums/MissionAdvancementPossibilities';
import OrderWorkunit from 'types/entities/OrderWorkunit';
import { setSnackbarAction } from 'store/actions/snackbarActions';
import MissionComment from 'types/entities/MissionComment';
import RatingPopup from './RatingPopup';
import { DeleteIcon } from 'components/CustomIcons';
import { ActionDescriptor } from 'types/enums/ActionDescriptor';

type QuickActionKey =
  | 'delete'
  | 'submission'
  | 'cancelsubmission'
  | 'validation'
  | 'cancelvalidation'
  | 'acceptation'
  | 'cancellation'
  | 'deliverablesassessment'
  | 'cancelacceptance'
  | 'restore';

type QuickActions = {
  cancellation: boolean;
  cancelsubmission: boolean;
  cancelvalidation: boolean;
  submission: boolean;
  validation: boolean;
  acceptation: boolean;
  delete: boolean;
  eval?: '5' | '4' | '3' | '2' | '1' | '0' | 'S' | null;
  comment?: MissionComment | null;
  cancelacceptance: boolean;
  restore: boolean;
};

type QuickActionsAffectations = {
  wu_parameters?: number;
  custom_wu_parameters?: number;
  ordered_wu_parameters?: number;
  cancellation: number;
  cancelsubmission: number;
  cancelvalidation: number;
  submission: number;
  validation: number;
  acceptation: number;
  delete: number;
  eval?: number;
  cancelacceptance: number;
  restore: number;
};

type QuickActionStatusDisabled = {
  wu_parameters?: boolean;
  custom_wu_parameters?: boolean;
  ordered_wu_parameters?: boolean;
  cancellation: boolean;
  cancelsubmission: boolean;
  cancelvalidation: boolean;
  submission: boolean;
  validation: boolean;
  acceptation: boolean;
  delete: boolean;
  eval?: boolean;
  cancelacceptance: boolean;
  restore: boolean;
};

const DeliverableQuickActions: React.FC = () => {
  type MenuAction =
    | 'submitDeliverables'
    | 'cancelSubmission'
    | 'validateDeliverables'
    | 'acceptDeliverables'
    | 'cancelDeliverables'
    | 'cancelValidation'
    | 'deleteDeliverables'
    | 'deliverablesAssessment'
    | 'cancelAcceptance'
    | 'restore';
  const [t] = useTranslation();
  const { dispatch } = useAppState();
  const {
    getDeliverableByListId,
    deleteDeliverables,
    cancelDeliverables,
    cancelAccetanceRestore,
    changeMissionAdvancement,
    setRatingDeliverables,
    addCommentDeliverable,
  } = useDeliverableService();
  const loadings = useSelector((state) => state.loadings);
  const appState = useSelector((state) => state.app);
  const state = useSelector((state) => state.deliverable);
  const clientConnection = useSelector((state) => state.deliverable.order?.client_connection);
  const [selectedDeliverables, selectSelectedDeliverables] = useState<OrderWorkunit[]>([]);
  const { canValidate, canSubmit, canCancel, canManagerRate, canManagerAccept } =
    useDeliverableRights(selectedDeliverables);
  const [quickActions, setQuickActions] = useState<QuickActions>({
    cancellation: false,
    cancelsubmission: false,
    cancelvalidation: false,
    submission: false,
    validation: false,
    acceptation: false,
    delete: false,
    cancelacceptance: false,
    restore: false,
  });
  const [quickActionsAffectation, setQuickActionsAffectation] = useState<QuickActionsAffectations>({
    cancellation: 0,
    cancelsubmission: 0,
    cancelvalidation: 0,
    submission: 0,
    validation: 0,
    acceptation: 0,
    delete: 0,
    eval: 0,
    cancelacceptance: 0,
    restore: 0,
  });
  const [quickActionStatusDisabled, setQuickActionStatusDisabled] = useState<QuickActionStatusDisabled>({
    cancellation: true,
    cancelsubmission: true,
    cancelvalidation: true,
    submission: true,
    validation: true,
    acceptation: true,
    delete: true,
    eval: false,
    cancelacceptance: true,
    restore: false,
  });
  const selectedDeliverablesForDuplicate = useSelector(duplicateSelectSelectedDeliverables);
  const [cancelReason, setCancelReason] = useState<string | null>(null);
  const [event, setEvent] = useState<MouseEvent | null>(null);
  const userIsAdmin = appState.roles?.includes('admin');
  const userIsConsultant = appState.roles?.toString().includes('consultant');
  const userIsBm = appState.roles?.toString().includes('bm');
  const userIsDC = appState.roles?.toString().includes('delivery_coordinator');
  const userIsDM = appState.roles?.toString().includes('delivery_manager');
  const userIsCustomer = appState.user?.is_client; // TODO use customer roles ?
  const canDeliverablesBeEvaluated = selectedDeliverables?.every(
    (mission) =>
      mission.mission_advancement_id &&
      mission.mission_advancement_id >= MissionAdvancementPossibilities.RESERVE_CLIENT &&
      !mission.rating
  );
  const userRights = useUserRoles();
  const { generateExcelReport, generateExcelExtract, duplicateDeliverables, isTnM } = useDeliverableService();
  const { canUseQuickActions, canEdit, canExport, canExportGR, canCreateDeliverableSheet, canDuplicate } =
    useDeliverableRights(state.selectedAllDeliverables && !userRights.isClient ? [] : selectedDeliverables);
  const isThereDuplicableDeliverables = selectedDeliverablesForDuplicate.some((deliv) => !deliv.workunit_id);
  const isEveryDeliverableDuplicable = selectedDeliverablesForDuplicate.every((deliv) => !deliv.workunit_id);
  const [triggerSubmit, setTriggerSubmit] = useState(false);
  const [isRatingPopupOpen, setIsRatingPopupOpen] = useState<boolean>(false);

  const [anchorPosition, setAnchorPosition] = useState<{ top: number; left: number } | null>(null);
  const [isMenuVisible, setIsMenuVisible] = useState(false);

  const selectModeAllDeliverables = useSelector((state) => state.deliverable.selectedAllDeliverables);
  const quickActionsButtonRef = useRef<HTMLButtonElement>(null);

  const orderType = useSelector((state) => state.deliverable.order?.order_type_id);

  const getSelectedDeliverableByListId = useCallback(async () => {
    // console.log('getDeliverableByListId called in useCallback');
    const deliverables = await getDeliverableByListId(
      state?.order?.id as number,
      state.selectedDeliverablesModel as number[]
    );
    selectSelectedDeliverables(deliverables);
  }, [getDeliverableByListId, state?.order?.id, state.selectedDeliverablesModel]);

  useEffect(() => {
    getSelectedDeliverableByListId();
  }, [getSelectedDeliverableByListId, state.selectedDeliverablesModel, state?.order?.id, isMenuVisible]);

  useEffect(() => {
    setQuickActionStatusDisabled({
      cancellation: canCancel,
      cancelsubmission: canCancelSubmission(),
      cancelvalidation: canCancelValidation(),
      submission: canSubmit,
      validation: canValidate,
      acceptation: isAcceptDisabled(),
      delete: isDeleteDeliverableDisable(),
      eval: canUserEvaluateDeliverables(),
      cancelacceptance: canCancelAcceptanceDeliverables(),
      restore: canRestoreDeliverables(),
    });
    if (!state.selectedAllDeliverables) {
      setQuickActionsAffectation({
        ...quickActionsAffectation,
        cancellation: countCancellableMissions(),
        submission: countSubmittableDeliverable(),
        validation: countValidableDeliverables(),
        acceptation: countCancellableValidatedDeliverables(),
        cancelsubmission: countCancellableSubmittedDeliverables(),
        cancelvalidation: countCancellableValidatedDeliverables(),
        delete: countDeletableDeliverable(),
        cancelacceptance: countAcceptedDeliverables(),
        restore: countRestoreDeliverables(),
      });
    }
  }, [selectedDeliverables, quickActions]);

  useEffect(() => {
    if (triggerSubmit && event) {
      submit(event);
      setTriggerSubmit(false);
    }
  }, [triggerSubmit]);

  const resetQuickAction = (actionKey: QuickActionKey) => {
    setQuickActions((prevQuickActions) => ({ ...prevQuickActions, [actionKey]: false }));
  };

  const countCancellableMissions = () => {
    let count = 0;
    if (userIsAdmin || userIsBm || userIsDC || userIsDM) {
      count = selectedDeliverables?.filter((mission) => !mission.label?.toLowerCase().includes('cancelled')).length;
    }
    if (userIsConsultant) {
      count = selectedDeliverables?.filter((mission) => !mission.mission_advancement_id).length;
    }
    return count;
  };

  function anyDeliverableHasUndefinedId(deliverables: OrderWorkunit[]) {
    return deliverables.some(
      (deliverable) =>
        !deliverable.consultant?.id || !deliverable.client?.id || deliverable?.delivery_manager?.length === 0
    );
  }

  // Check if any deliverable meets the acceptance status criteria
  function anyDeliverableAboveAcceptanceStatus(deliverables: OrderWorkunit[]) {
    return deliverables.some(
      (deliverable) =>
        deliverable.mission_advancement_id &&
        deliverable.mission_advancement_id >= MissionAdvancementPossibilities.ACCEPTATION_DC &&
        (!deliverable.consultant?.id || !deliverable.client?.id)
    );
  }

  const handleActions = async (item: any) => {
    if (item.loadingName) dispatch(addLoadingAction(`action${item.loadingName}`));
    await item.action();
    if (item.loadingName) dispatch(removeLoadingAction(`action${item.loadingName}`));
  };

  const exportExcelGR = useCallback(async () => {
    await generateExcelReport('financial-reports');
  }, [generateExcelReport]);

  const exportExcelReport = useCallback(async () => {
    await generateExcelReport('deliverables');
  }, [generateExcelReport]);

  const createSheetReport = useCallback(async () => {
    dispatch(openDialogAction({ name: 'deliverableCreateReportConfirm' }));
  }, [dispatch]);

  const openEditDeliverableDrawer = useCallback(async () => {
    dispatch(
      openDrawerAction({
        name: 'deliverableEdit',
        data: state.selectedDeliverablesModel,
      })
    );
  }, [dispatch, state.selectedDeliverablesModel]);

  const duplicateDeliverable = useCallback(async () => {
    duplicateDeliverables(selectedDeliverables);
  }, [duplicateDeliverables, selectedDeliverables]);

  const canCancelSubmission = () => {
    if (state.selectedAllDeliverables) {
      return true;
    }
    if (userIsAdmin || userIsBm || userIsDC || userIsDM || userIsConsultant) {
      if (countClientAcceptanceRecords() > 0) {
        return true;
      }
      return !selectedDeliverables?.every(
        (m: { mission_advancement_id?: number | null }) =>
          m.mission_advancement_id && m.mission_advancement_id === MissionAdvancementPossibilities.SUBMITTED
      );
    }
    return false;
  };

  const canCancelAcceptanceDeliverables = () => {
    if (state.selectedAllDeliverables) {
      return true;
    }

    if (userIsAdmin || userIsBm || userIsDC || userIsDM) {
      return !selectedDeliverables?.some(
        (deliverable) =>
          !deliverable.mission_advancement_id ||
          (deliverable.mission_advancement_id &&
            ![
              MissionAdvancementPossibilities.REFUSED_CLIENT,
              MissionAdvancementPossibilities.RESERVE_CLIENT,
              MissionAdvancementPossibilities.ACCEPTATION_CLIENT,
            ].includes(deliverable.mission_advancement_id))
      );
    }
    return false;
  };

  const canRestoreDeliverables = () => {
    if (state.selectedAllDeliverables) {
      return true;
    }

    if (userIsAdmin || userIsBm || userIsDC || userIsDM) {
      return selectedDeliverables?.every((d) => !d.label?.toLowerCase().includes('cancelled'));
    }
    return false;
  };

  const canCancelValidation = () => {
    if (state.selectedAllDeliverables) {
      return true;
    }

    if (userIsAdmin || userIsBm || userIsDC || userIsDM) {
      if (countClientAcceptanceRecords() > 0) {
        return true;
      }
      return !selectedDeliverables.every(
        (m: { mission_advancement_id?: number | null }) =>
          m.mission_advancement_id &&
          m.mission_advancement_id >= MissionAdvancementPossibilities.RESERVE_DC &&
          m.mission_advancement_id <= MissionAdvancementPossibilities.ACCEPTATION_DC

      );
    }
    return false;
  };


  const isAcceptDisabled = () => {
    if (state.selectedAllDeliverables) {
      return true;
    }
    if (userIsCustomer || (!clientConnection && canManagerAccept)) {
      return !selectedDeliverables.every(
        (m) =>
          m.mission_advancement_id &&
          m.mission_advancement_id >= MissionAdvancementPossibilities.RESERVE_DC &&
          ![
            MissionAdvancementPossibilities.REFUSED_CLIENT,
            MissionAdvancementPossibilities.RESERVE_CLIENT,
            MissionAdvancementPossibilities.ACCEPTATION_CLIENT,
          ].includes(m.mission_advancement_id)
      );
    }
    return true;
  };

  const isDeleteDeliverableDisable = () => {
    if (state.selectedAllDeliverables) {
      return true;
    }
    let returnFlag1 = true;
    let returnFlag2 = true;
    let returnFlag3 = true;
    if (userIsConsultant)
      returnFlag1 = !selectedDeliverables.every(
        (d) =>
          !d.mission_advancement_id &&
          !d.origin.includes('quote') &&
          !d.origin.includes('custom') &&
          !d.origin.includes('catalog') &&
          !d.label?.includes('added')
      );

    // console.log(`returnFlag1 : ${returnFlag1}`);

    if (userIsBm || userIsDC || userIsDM || userIsAdmin) {
      returnFlag2 = !selectedDeliverables.every(
        (d) =>
          !d.origin.includes('quote') &&
          !d.origin.includes('custom') &&
          !d.origin.includes('catalog') &&
          !d.label?.includes('added') &&
          (!d.mission_advancement_id ||
            (d.mission_advancement_id && d.mission_advancement_id > MissionAdvancementPossibilities.SUBMITTED))
      );
      // console.log(`returnFlag2 : ${returnFlag2}`);
    }
    returnFlag3 = !selectedDeliverables.every(
      (d) =>
        !d.mission_advancement_id &&
        (d.origin.includes('custom') || (d.origin.includes('catalog') && d.label?.includes('added')))
    );

    if (!returnFlag1) return returnFlag1;
    if (!returnFlag2) return returnFlag2;
    return returnFlag3;
  };

  const canUserEvaluateDeliverables = () => {
    if (state.selectedAllDeliverables) {
      return true;
    }
    if (userIsCustomer) {
      return selectedDeliverables.every(
        (m) =>
          m.mission_advancement_id &&
          m.mission_advancement_id >= MissionAdvancementPossibilities.RESERVE_CLIENT &&
          (!m.rating || m.rating === '')
      );
    }
    return true;
  };

  const countDeletableDeliverable = () =>
    selectedDeliverables.filter((m) => {
      if (m.origin === 'quote') return false;
      if (m.origin.toLowerCase() === 'custom' || m.label?.toLowerCase() === 'added') {
        if (m.mission_advancement_id && m.mission_advancement_id >= MissionAdvancementPossibilities.SUBMITTED)
          return false;
      }
      if (m.mission_advancement_id && m.mission_advancement_id < MissionAdvancementPossibilities.RESERVE_DC)
        if (userIsAdmin || userIsDC || userIsBm || userIsDM) return true;
      if (!m.mission_advancement_id) {
        if (userIsConsultant && m.consultant?.id === appState.user?.id) {
          return true;
        }
        if (userIsAdmin || userIsDC || userIsBm || userIsDM) return true;
      }
      return false;
    }).length;

  const countSubmittableDeliverable = () => {
    let count = 0;
    if (userIsAdmin || userIsBm || userIsDC || userIsConsultant) {
      count = selectedDeliverables?.filter((m) => !m.mission_advancement_id).length;
    }
    return count;
  };

  const countCancellableSubmittedDeliverables = () => {
    let count = 0;
    if (userIsAdmin || userIsBm || userIsDC || userIsDM || userIsConsultant) {
      count = selectedDeliverables?.filter(
        (m) => m.mission_advancement_id && m.mission_advancement_id === MissionAdvancementPossibilities.SUBMITTED
      ).length;
    }
    return count;
  };

  const countCancellableValidatedDeliverables = () => {
    let count = 0;
    if (userIsAdmin || userIsBm || userIsDC || userIsDM) {
      count = selectedDeliverables?.filter(
        (m) =>
          m.mission_advancement_id &&
          (m.mission_advancement_id === MissionAdvancementPossibilities.ACCEPTATION_DC ||
            m.mission_advancement_id === MissionAdvancementPossibilities.RESERVE_DC)
      ).length;
    }
    return count;
  };

  const countAcceptedDeliverables = () => {
    let count = 0;
    if (userIsAdmin || userIsBm || userIsDC || userIsDM) {
      count = selectedDeliverables?.filter(
        (m) => m.mission_advancement_id && m.mission_advancement_id >= MissionAdvancementPossibilities.REFUSED_CLIENT
      ).length;
    }
    return count;
  };

  const countValidableDeliverables = () => {
    let count = 0;
    if (userIsAdmin || userIsBm || userIsDC || userIsDM) {
      count = selectedDeliverables?.filter(
        (m) => m.mission_advancement_id && m.mission_advancement_id === MissionAdvancementPossibilities.SUBMITTED
      ).length;
    }
    return count;
  };

  const countClientAcceptanceRecords = () => {
    let count = 0;
    if (userIsAdmin || userIsBm || userIsDC || userIsDM || userIsConsultant) {
      count = selectedDeliverables?.filter(
        (m) => m.mission_advancement_id && m.mission_advancement_id > MissionAdvancementPossibilities.ACCEPTATION_DC
      ).length;
    }
    return count;
  };

  const countRestoreDeliverables = () => {
    let count = 0;
    if (userIsAdmin || userIsBm || userIsDC || userIsDM) {
      count = selectedDeliverables?.filter((m) => m.label?.toLowerCase().includes('cancelled')).length;
    }
    return count;
  };

  const actions = useMemo(
    () => [
      {
        action: duplicateDeliverable,
        permission: canDuplicate && !selectModeAllDeliverables && isEveryDeliverableDuplicable, // isThereDuplicableDeliverables,
        label: t('Duplicate'),
        loadingName: 'duplicateDeliverable',
        icon: <FileCopy />,
        tooltip: !isEveryDeliverableDuplicable ? t('Partial_Duplicate') : '',
      },
      {
        action: generateExcelExtract,
        permission: selectedDeliverables?.length > 0 || selectModeAllDeliverables,
        label: t('Generate_Sheet'),
        loadingName: 'generateExcelExtract',
        icon: <GetApp />,
      },
      {
        action: exportExcelGR,
        permission: canExportGR && !isTnM(),
        label: t('Export GR excel'),
        loadingName: 'exportExcelGR',
      },
      {
        action: exportExcelReport,
        permission: state.currentTableType === DeliverableTableType.TECHNICAL && canExport,
        label: t('Export excel report'),
        loadingName: 'exportExcelReport',
        icon: <Edit />,
      },
      /* {
        action: createSheetReport,
        permission: state.currentTableType === DeliverableTableType.TECHNICAL && canCreateDeliverableSheet,
        label: t('Create_Report'),
        loadingName: 'createSheetReport',
      }, */
      {
        action: openEditDeliverableDrawer,
        permission: state.currentTableType === DeliverableTableType.TECHNICAL && canEdit,
        label: t('Mass edit'),
      },
    ],
    [
      exportExcelGR,
      canExportGR,
      t,
      exportExcelReport,
      state.currentTableType,
      canExport,
      createSheetReport,
      canCreateDeliverableSheet,
      openEditDeliverableDrawer,
      canEdit,
      canUseQuickActions,
      isThereDuplicableDeliverables,
      generateExcelExtract,
      isTnM,
      canDuplicate,
      selectModeAllDeliverables,
      isEveryDeliverableDuplicable,
    ]
  );

  const NavigationBottom = styled(LoadingButton)<ButtonProps>(({ theme }) => ({
    color: theme.palette.primary.main,
    backgroundColor: '#FFFFFF',
    borderColor: '#FFFFFF',
    '&:hover': {
      backgroundColor: '#FFFFFF',
    },
  }));

  const actionsAvailables = useMemo(() => actions.filter((item) => item.permission !== false), [actions]);
  const selectedItems = () => {
    if (state.selectedAllDeliverables) {
      if (state.selectedDeliverablesModel.length > 0) {
        return `${state.totalItems - state.selectedDeliverablesModel.length} items selected (${state.selectedDeliverablesModel.length
          } excluded)`;
      }
      return `All selected (${state.totalItems} items)`;
    }

    return `${state.selectedDeliverablesModel?.length} ${t('Items_Selected')}`;
  };

  // added condition for client
  const handleOpen = useCallback(
    (event: any) => {
      event.preventDefault();
      if (quickActionsButtonRef.current) {
        const rect = quickActionsButtonRef.current.getBoundingClientRect();
        const userIsClient = appState?.user?.is_client ?? false;
        if (!userIsClient) {
          setAnchorPosition({
            top: rect.bottom - 320,
            left: rect.left,
          });
        } else {
          setAnchorPosition({
            top: rect.bottom - 180,
            left: rect.left,
          });
        }

        setIsMenuVisible(true);
      }
    },
    [appState, quickActionsButtonRef]
  );

  const handleClose = useCallback(() => {
    setAnchorPosition(null);
    setIsMenuVisible(false);
  }, []);

  const handleMenuItemClick = (action: MenuAction, event: MouseEvent) => {
    // Handle menu item action here
    switch (action) {
      case 'deleteDeliverables':
        handleDeleteDeliverables(event);
        break;
      case 'submitDeliverables':
        handleSubmitDeliverables(event);
        break;
      case 'cancelSubmission':
        handleCancelSubmission(event);
        break;
      case 'validateDeliverables':
        handleValidateDeliverables(event);
        break;
      case 'cancelValidation':
        handleCancelValidation(event);
        break;
      case 'acceptDeliverables':
        handleAcceptanceDeliverables(event);
        break;
      case 'cancelDeliverables':
        handleCancelDeliverables(event);
        break;
      case 'deliverablesAssessment':
        handleDeliverablesAssessment(event);
        break;
      case 'cancelAcceptance':
        handleCancelAcceptance(event);
        break;
      case 'restore':
        handleRestore(event);
        break;
      default:
        console.log('handleMenuItemClick');
    }

    handleClose();
  };

  const submit = async (event: MouseEvent) => {
    if (state.selectedAllDeliverables) {
      return;
    }

    // ED-36 changes
    let isClientDeliverableAboveAcceptanceStatus = false;
    if (userIsCustomer) {
      isClientDeliverableAboveAcceptanceStatus = anyDeliverableAboveAcceptanceStatus(selectedDeliverables);

      if (!isClientDeliverableAboveAcceptanceStatus) {
        isClientDeliverableAboveAcceptanceStatus = true;
      }
    }

    const orderWorkunitsIds = state.selectedDeliverablesModel as OrderWorkunit['id'][];

    if (quickActions.submission) {
      await changeMissionAdvancement(
        orderWorkunitsIds,
        MissionAdvancementPossibilities.SUBMITTED,
        true,
        state.selectedAllDeliverables
      );
      resetQuickAction('submission');
    }

    if (quickActions.delete) {
      await deleteDeliverables(orderWorkunitsIds, state.selectedAllDeliverables);
      resetQuickAction('delete');
    }

    if (quickActions.validation) {
      await changeMissionAdvancement(
        orderWorkunitsIds,
        MissionAdvancementPossibilities.ACCEPTATION_DC,
        true,
        state.selectedAllDeliverables
      );
      resetQuickAction('validation');
      // console.log('quickActions.validation');
    }

    if (quickActions.acceptation) {
      await changeMissionAdvancement(
        orderWorkunitsIds,
        MissionAdvancementPossibilities.ACCEPTATION_CLIENT,
        true,
        state.selectedAllDeliverables
      );
      resetQuickAction('acceptation');
      // console.log('quickActions.acceptation');
    }

    if (quickActions.eval) {
      if (quickActions.acceptation) {
        // console.log('quickActions.acceptation entering here');
        const deliverablesRatable = selectedDeliverables.map((d) => ({
          ...d,
          mission_advancement_id: MissionAdvancementPossibilities.ACCEPTATION_CLIENT,
        }));
        if (quickActions.comment && quickActions.comment.comment !== undefined) {
          await Promise.all(
            deliverablesRatable.map(async (deliverable) => {
              if (quickActions.comment) {
                await addCommentDeliverable(deliverable, {
                  comment: quickActions.comment.comment,
                  is_private: quickActions.comment.is_private ?? false,
                });
              }
            })
          );
        }

        await setRatingDeliverables(deliverablesRatable, quickActions.eval, null, state.selectedAllDeliverables);
        setQuickActions((prevActions) => ({
          ...prevActions,
          eval: null,
        }));

        // console.log('setRatingDeliverables');
      } else {
        if (quickActions.comment && quickActions.comment.comment !== undefined) {
          await Promise.all(
            selectedDeliverables.map(async (deliverable) => {
              if (quickActions.comment) {
                await addCommentDeliverable(deliverable, {
                  comment: quickActions.comment.comment,
                  is_private: quickActions.comment.is_private ?? false,
                });
              }
            })
          );
        }

        await setRatingDeliverables(selectedDeliverables, quickActions.eval, null, state.selectedAllDeliverables);
        setQuickActions((prevActions) => ({
          ...prevActions,
          eval: null,
        }));
      }
    }

    if (
      (quickActions.cancelsubmission && quickActions.cancelvalidation && !quickActions?.cancellation) ||
      (quickActions.cancelsubmission && countCancellableValidatedDeliverables() > 0)
    ) {
      event.preventDefault();
      const confirmOk = async () => {
        await changeMissionAdvancement(orderWorkunitsIds, null);
      };
      dispatch(
        openDialogAction({
          name: 'deliverableSubmissionCancellation',
          data: { confirm: confirmOk },
        })
      );
    } else if (quickActions.cancelsubmission) {
      await changeMissionAdvancement(orderWorkunitsIds, null);
      setQuickActions((prevQuickActions) => ({ ...prevQuickActions, cancelsubmission: false }));
    } else if (quickActions.cancelvalidation) {
      // if (countCancellableSubmittedDeliverables() > 0 || countCancellableMissions() > 0) {
      //   dispatch(
      //     setSnackbarAction({
      //       message:
      //         'Cancel validation could not be done as there are other selected submitted/cancelled deliverables.',
      //       open: true,
      //       severity: 'info',
      //     })
      //   );
      //   setTriggerSubmit(false);
      //   return;
      // }
      await changeMissionAdvancement(orderWorkunitsIds, MissionAdvancementPossibilities.SUBMITTED);
      resetQuickAction('cancelvalidation');
    }

    if (quickActions?.cancellation && cancelReason) {
      const orderWorkunitsIds = selectedDeliverables.map((orderWorkunit) => orderWorkunit.id);
      await cancelDeliverables(orderWorkunitsIds, cancelReason, state.selectedAllDeliverables);
      resetQuickAction('cancellation');
    }

    if (quickActions?.cancelacceptance) {
      const orderWorkunitsIds = selectedDeliverables.map((orderWorkunit) => orderWorkunit.id);
      await cancelAccetanceRestore(
        orderWorkunitsIds,
        ActionDescriptor.DeliverableCancelAcceptance,
        state.selectedAllDeliverables
      );
      resetQuickAction('cancelacceptance');
    }

    if (quickActions?.restore) {
      const orderWorkunitsIds = selectedDeliverables.map((orderWorkunit) => orderWorkunit.id);
      await cancelAccetanceRestore(
        orderWorkunitsIds,
        ActionDescriptor.DeliverableBulkRestore,
        state.selectedAllDeliverables
      );
      resetQuickAction('restore');
    }

    setTriggerSubmit(false);
  };

  const handleQuickAction = (actionKey: QuickActionKey, message: string, title: string) => {
    const confirmOk = async (cancelReason?: string) => {
      setQuickActions((prevQuickActions) => {
        const newQuickActions = { ...prevQuickActions, [actionKey]: true };
        return newQuickActions;
      });
      if (cancelReason) {
        setCancelReason(cancelReason);
      }

      setTriggerSubmit(true);
    };

    dispatch(
      openDialogAction({
        name: 'deliverableConfirmationDialog',
        data: {
          message,
          title,
          confirm: confirmOk,
        },
      })
    );
  };

  const handleDeleteDeliverables = (event: MouseEvent) => {
    setEvent(event);
    handleQuickAction('delete', 'Are you sure to delete the selected deliverables?', 'Delete Deliverables');
  };

  const handleSubmitDeliverables = (event: MouseEvent) => {
    setEvent(event);
    handleQuickAction('submission', 'Submit the selected deliverables ?', 'Submit Deliverables');
  };

  const handleCancelSubmission = (event: MouseEvent) => {
    setEvent(event);
    handleQuickAction('cancelsubmission', 'Are you sure to cancel the submission ?', 'Cancel Submission');
  };

  const handleValidateDeliverables = (event: MouseEvent) => {
    setEvent(event);
    handleQuickAction('validation', 'Validate the selected deliverables ?', 'Validate Deliverables');
  };

  const handleCancelValidation = (event: MouseEvent) => {
    setEvent(event);
    handleQuickAction('cancelvalidation', 'Are you sure to cancel validation ?', 'Cancel Validation');
  };

  const handleAcceptanceDeliverables = (event: MouseEvent) => {
    // setEvent(event);
    const orderWorkunitsIds = state.selectedDeliverablesModel as OrderWorkunit['id'][];
    const accept = 7;
    const refuse = 5;
    dispatch(
      openDialogAction({
        name: 'deliverableAdvancementRatingDialog',
        data: {
          acceptState: true,
          accept,
          refuse,
          withReserve: [6],
          activeDeliverable: orderWorkunitsIds,
          reservationButton: true,
          quickActionfn: 'multiple',
        },
      })
    );
    // handleQuickAction('acceptation', 'Accept the selected deliverables ?', 'Accept Deliverables');
  };

  const handleCancelDeliverables = (event: MouseEvent) => {
    setEvent(event);
    handleQuickAction('cancellation', 'Are you sure to cancel the selected deliverables ?', 'Cancel Deliverables');
  };

  const handleCancelAcceptance = (event: MouseEvent) => {
    setEvent(event);
    handleQuickAction('cancelacceptance', 'Are you sure to cancel the acceptance ?', 'Cancel Acceptance');
  };

  const handleRestore = (event: MouseEvent) => {
    setEvent(event);
    handleQuickAction('restore', 'Are you sure to restore the selected deliverables ?', 'Restore Deliverables');
  };
  const handleDeliverablesAssessment = (event: MouseEvent) => {
    setEvent(event);
    setIsRatingPopupOpen(true);
  };
  // console.log(`quickActionStatusDisabled.submission `, quickActionStatusDisabled.submission);

  const handleCloseRatingPopup = () => {
    setIsRatingPopupOpen(false);
  };
  const handleSubmitRatingPopup = () => {
    setTriggerSubmit(true);
  };

  const isLoadingApi = !!loadings.api;
  return (
    <span>
      {canUseQuickActions &&
        actionsAvailables.length > 0 &&
        (!!state.selectedDeliverablesModel.length || state.selectedAllDeliverables) && (
          <BottomNavigation
            sx={{
              borderRadius: '12px',
              backgroundColor: 'primary.main',
              position: 'fixed',
              flexDirection: { xs: 'column', md: 'row' },
              bottom: '10px',
              right: '25px',
              left: { xs: '25px', md: '170px' },
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              p: 2,
              color: '#FFFFFF',
              height: 'auto',
            }}
          >
            <Typography sx={{ fontWeight: 500, mb: { xs: 1, md: 0 } }}>{selectedItems()}</Typography>
            <Tooltip title="Affect every page">
              <NavigationBottom
                onClick={() => {
                  dispatch(selectAllDeliverablesAction(false));
                  dispatch(selectDeliverablesAction([]));
                }}
                sx={{ ml: 1, mb: { xs: 1, lg: 0 } }}
                size="small"
              >
                {t('Unselect all')}
              </NavigationBottom>
            </Tooltip>
            <Box
              sx={{ ml: 'auto', display: 'flex', justifyContent: { xs: 'center', md: 'flex-end' }, flexWrap: 'wrap' }}
            >
              {actionsAvailables.map((item) => (
                <NavigationBottom
                  key={item.label}
                  onClick={() => handleActions(item)}
                  sx={{ ml: 1, mb: { xs: 1, lg: 0 } }}
                  size="small"
                  loading={!!loadings[`action${item.loadingName}`]}
                >
                  {item.label}
                </NavigationBottom>
              ))}
              {!state.selectedAllDeliverables && (
                <NavigationBottom
                  ref={quickActionsButtonRef}
                  aria-label="settings"
                  aria-controls="simple-menu"
                  aria-haspopup="true"
                  onClick={handleOpen}
                  sx={{ ml: 1, mb: { xs: 1, lg: 0 } }}
                  size="small"
                >
                  Quick actions
                </NavigationBottom>
              )}
              <Menu
                anchorReference="anchorPosition"
                anchorPosition={
                  anchorPosition !== null ? { top: anchorPosition.top, left: anchorPosition.left } : undefined
                }
                open={Boolean(anchorPosition)}
                onClose={handleClose}
              >
                <MenuList>
                  {!userIsCustomer && (
                    <MenuItem
                      onClick={(event) => handleMenuItemClick('submitDeliverables', event)}
                      disabled={isLoadingApi || !quickActionStatusDisabled.submission}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {/* <Box sx={{ mr: 1 }}>{t<string>('Deliverable_submission')}</Box> */}
                        <CheckCircle
                          sx={{
                            color: !quickActionStatusDisabled.submission ? 'text.disabled' : 'text.primary',
                            mr: 1,
                          }}
                        />
                        <Box sx={{ mr: 1 }}>{t<string>('Submit')}</Box>

                        {quickActionsAffectation.submission !== 0 && (
                          <Chip
                            size="small"
                            color="primary"
                            label={quickActionsAffectation.submission}
                            sx={{ fontSize: '1rem', fontWeight: 500 }}
                          />
                        )}
                      </Box>
                    </MenuItem>
                  )}
                  {(userIsAdmin || userIsBm || userIsDC || userIsDM || userIsConsultant) && (
                    <MenuItem
                      onClick={(event) => handleMenuItemClick('cancelSubmission', event)}
                      disabled={isLoadingApi || quickActionStatusDisabled.cancelsubmission}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <History
                          sx={{ color: quickActionStatusDisabled.cancelsubmission ? 'text.disabled' : 'error', mr: 1 }}
                        />
                        <Box sx={{ mr: 1, color: 'error.main' }}>{t<string>('Cancel_Submission')}</Box>
                        {quickActionsAffectation.cancelsubmission !== 0 && (
                          <Chip
                            size="small"
                            color="primary"
                            label={quickActionsAffectation.cancelsubmission}
                            sx={{ fontSize: '1rem', fontWeight: 500 }}
                          />
                        )}
                      </Box>
                    </MenuItem>
                  )}
                  {!userIsCustomer && !userIsConsultant && (
                    <MenuItem
                      onClick={(event) => handleMenuItemClick('validateDeliverables', event)}
                      disabled={isLoadingApi || !quickActionStatusDisabled.validation}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {/* <Box sx={{ mr: 1 }}>{t<string>('Deliverables_validation')}</Box> */}
                        <CheckCircle
                          sx={{
                            color: !quickActionStatusDisabled.validation ? 'text.disabled' : 'text.primary',
                            mr: 1,
                          }}
                        />
                        <Box sx={{ mr: 1 }}>{t<string>('Validate_Tm')}</Box>
                        {quickActionsAffectation.validation !== 0 && (
                          <Chip
                            size="small"
                            color="primary"
                            label={quickActionsAffectation.validation}
                            sx={{ fontSize: '1rem', fontWeight: 500 }}
                          />
                        )}
                      </Box>
                    </MenuItem>
                  )}
                  {(userIsAdmin || userIsBm || userIsDC || userIsDM) && (
                    <MenuItem
                      onClick={(event) => handleMenuItemClick('cancelValidation', event)}
                      disabled={isLoadingApi || quickActionStatusDisabled.cancelvalidation}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <History
                          sx={{ color: quickActionStatusDisabled.cancelvalidation ? 'text.disabled' : 'error', mr: 1 }}
                        />
                        <Box sx={{ mr: 1, color: 'error.main' }}>{t<string>('Cancel_Validation')}</Box>
                        {quickActionsAffectation.cancelvalidation !== 0 && (
                          <Chip
                            size="small"
                            color="primary"
                            label={quickActionsAffectation.cancelvalidation}
                            sx={{ fontSize: '1rem', fontWeight: 500 }}
                          />
                        )}
                      </Box>
                    </MenuItem>
                  )}
                  {(userIsCustomer || !clientConnection) && (
                    <MenuItem
                      onClick={(event) => handleMenuItemClick('acceptDeliverables', event)}
                      disabled={isLoadingApi || quickActionStatusDisabled.acceptation}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <CheckCircle
                          sx={{ color: quickActionStatusDisabled.acceptation ? 'text.disabled' : 'error', mr: 1 }}
                        />
                        <Box sx={{ mr: 1 }}>{t<string>('Accept')}</Box>
                        {quickActionsAffectation.acceptation !== 0 && (
                          <Chip
                            size="small"
                            color="primary"
                            label={quickActionsAffectation.acceptation}
                            sx={{ fontSize: '1rem', fontWeight: 500 }}
                          />
                        )}
                      </Box>
                    </MenuItem>
                  )}
                  {(userIsAdmin || userIsBm || userIsDC || userIsDM) && (
                    <MenuItem
                      onClick={(event) => handleMenuItemClick('cancelAcceptance', event)}
                      disabled={isLoadingApi || !quickActionStatusDisabled.cancelacceptance}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <History
                          sx={{ color: quickActionStatusDisabled.cancelacceptance ? 'text.disabled' : 'error', mr: 1 }}
                        />
                        <Box sx={{ mr: 1, color: 'error.main' }}>{t<string>('Cancel_Acceptance')}</Box>
                        {quickActionsAffectation.cancelacceptance !== 0 && (
                          <Chip
                            size="small"
                            color="primary"
                            label={quickActionsAffectation.cancelacceptance}
                            sx={{ fontSize: '1rem', fontWeight: 500 }}
                          />
                        )}
                      </Box>
                    </MenuItem>
                  )}
                  {!userIsCustomer && (
                    <MenuItem
                      onClick={(event) => handleMenuItemClick('cancelDeliverables', event)}
                      disabled={isLoadingApi || !quickActionStatusDisabled.cancellation}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Block
                          sx={{ color: quickActionStatusDisabled.cancellation ? 'text.disabled' : 'error', mr: 1 }}
                        />
                        <Box sx={{ mr: 1, color: 'error.main' }}>{t<string>('Cancel')}</Box>
                        {quickActionsAffectation.cancellation !== 0 && (
                          <Chip
                            size="small"
                            color="primary"
                            label={quickActionsAffectation.cancellation}
                            sx={{ fontSize: '1rem', fontWeight: 500 }}
                          />
                        )}
                      </Box>
                    </MenuItem>
                  )}

                  {(userIsAdmin || userIsBm || userIsDC || userIsDM || userIsConsultant) && (
                    <MenuItem
                      onClick={(event) => handleMenuItemClick('restore', event)}
                      disabled={isLoadingApi || quickActionStatusDisabled.restore}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <CheckCircle
                          sx={{ color: quickActionStatusDisabled.restore ? 'text.disabled' : 'error', mr: 1 }}
                        />
                        <Box sx={{ mr: 1 }}>{t<string>('Restore')}</Box>
                        {quickActionsAffectation.restore !== 0 && (
                          <Chip
                            size="small"
                            color="primary"
                            label={quickActionsAffectation.restore}
                            sx={{ fontSize: '1rem', fontWeight: 500 }}
                          />
                        )}
                      </Box>
                    </MenuItem>
                  )}

                  {!userIsCustomer && (
                    <MenuItem
                      onClick={(event) => handleMenuItemClick('deleteDeliverables', event)}
                      disabled={isLoadingApi || quickActionStatusDisabled.delete}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <DeleteIcon color="error" sx={{ mr: 1 }} />
                        <Box sx={{ mr: 1, color: 'error.main' }}>{t<string>('Delete')}</Box>
                        {quickActionsAffectation.delete !== 0 && (
                          <Chip
                            size="small"
                            color="primary"
                            label={quickActionsAffectation.delete}
                            sx={{ fontSize: '1rem', fontWeight: 500 }}
                          />
                        )}
                      </Box>
                    </MenuItem>
                  )}
                  {/* {(!userIsCustomer || (!clientConnection && canManagerRate)) && !state.selectedAllDeliverables && (
                    <MenuItem
                      onClick={(event) => handleMenuItemClick('deliverablesAssessment', event)}
                      disabled={isLoadingApi || !canDeliverablesBeEvaluated || quickActions.acceptation}
                    >
                      Deliverables assessment
                    </MenuItem>
                  )} */}
                </MenuList>
              </Menu>
            </Box>
            <RatingPopup
              open={isRatingPopupOpen}
              onClose={handleCloseRatingPopup}
              onSubmit={handleSubmitRatingPopup}
              setQuickActions={setQuickActions}
            />
          </BottomNavigation>
        )}
    </span>
  );
};

export default DeliverableQuickActions;
